import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { paintings } from '../data/paintings';
import ArtworkModal from '../components/ArtworkModal';

const Gallery = () => {
  const { t, lang } = useLanguage();
  const [filter, setFilter] = useState('all');
  const [selectedArtwork, setSelectedArtwork] = useState(null);

  const filteredArtworks = paintings.filter(artwork => {
    if (filter === 'all') return true;
    if (filter === 'available') return artwork.available;
    if (filter === 'sold') return !artwork.available;
    return true;
  });

  return (
    <main className="min-h-screen pt-40 pb-32 bg-gallery-bg">
      <div className="max-w-7xl mx-auto px-6">
        <header className="mb-20 text-center max-w-2xl mx-auto">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-5xl md:text-6xl font-serif mb-8 text-gallery-text italic"
          >
            {t('gallery.title')}
          </motion.h1>
          
          <nav className="flex justify-center flex-wrap gap-8 text-xs uppercase tracking-[0.2em] font-medium" aria-label="Filtro de galería">
            {['all', 'available', 'sold'].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`group relative py-2 transition-colors ${filter === f ? 'text-gallery-accent' : 'text-gallery-muted hover:text-gallery-text'}`}
                aria-current={filter === f ? 'page' : undefined}
              >
                {t(`gallery.filter${f.charAt(0).toUpperCase() + f.slice(1)}`)}
                <span className={`absolute bottom-0 left-0 w-full h-[1px] bg-gallery-accent transition-transform duration-300 origin-left ${filter === f ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'}`} />
              </button>
            ))}
          </nav>
        </header>

        <AnimatePresence mode="wait">
          {filteredArtworks.length === 0 ? (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="text-center text-gallery-muted py-32 font-serif text-xl italic"
            >
              {t('gallery.noResults')}
            </motion.div>
          ) : (
            <motion.div 
              layout 
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 lg:gap-12"
            >
              {filteredArtworks.map((artwork, index) => (
                <ArtworkCard 
                  key={artwork.id} 
                  artwork={artwork} 
                  index={index} 
                  lang={lang} 
                  t={t} 
                  onClick={() => setSelectedArtwork(artwork)} 
                />
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <ArtworkModal artwork={selectedArtwork} onClose={() => setSelectedArtwork(null)} />
    </main>
  );
};

const ArtworkCard = ({ artwork, index, lang, t, onClick }) => {
  const title = lang === 'en' ? artwork.titleEn || artwork.title : artwork.title;
  
  return (
    <motion.article
      layout
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: (index % 6) * 0.1 }}
      className="group cursor-pointer flex flex-col h-full"
      onClick={onClick}
    >
      <div className="relative overflow-hidden bg-gallery-surface aspect-[4/5] shadow-sm">
        <img 
          src={artwork.image} 
          alt={`${title} - Obra original de Gisselle Mendez`} 
          className="w-full h-full object-cover transition-transform duration-[1.2s] group-hover:scale-105" 
          loading="lazy"
        />
        <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-center justify-center">
          <span className="text-white text-xs uppercase tracking-[0.3em] border-b border-white/50 pb-1">
            Ver detalle
          </span>
        </div>
        {!artwork.available && (
          <div className="absolute top-4 right-4 bg-black/60 backdrop-blur-sm text-white text-[9px] uppercase tracking-widest px-2 py-1">
            {t('artwork.soldOut')}
          </div>
        )}
      </div>

      <div className="mt-6">
        <div className="flex justify-between items-start gap-4">
          <div>
            <h3 className="font-serif text-xl leading-tight mb-1">
              {title}
            </h3>
            <p className="text-[10px] uppercase tracking-[0.15em] text-gallery-muted">
              {artwork.technique} — {artwork.size}
            </p>
          </div>
          <div className="text-right">
            <p className="font-serif text-lg text-gallery-text">
              {!artwork.available ? (
                <span className="text-gallery-muted italic text-sm">{t('artwork.soldOut')}</span>
              ) : (
                `€${artwork.price.toLocaleString()}`
              )}
            </p>
          </div>
        </div>
      </div>
    </motion.article>
  );
};

export default Gallery;